import { useCallback, useEffect, useState } from 'react';

/*
 * Light / dark toggle.
 *
 * The theme is applied as `data-theme` on <html> and read by App.css. With no
 * stored preference the attribute is absent and the CSS falls back to
 * prefers-color-scheme, so the site respects the OS setting until the reader
 * overrides it.
 *
 * The initial attribute is set by a small inline script in public/index.html —
 * doing it here in an effect would paint the wrong theme for one frame.
 */

const STORAGE_KEY = 'theme';

const systemPrefersDark = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-color-scheme: dark)').matches;

const currentTheme = () => {
  if (typeof document === 'undefined') return 'light';
  return document.documentElement.dataset.theme || (systemPrefersDark() ? 'dark' : 'light');
};

function ThemeToggle() {
  const [theme, setTheme] = useState('light');

  // Sync from the DOM after mount so prerendered HTML and the client agree.
  useEffect(() => setTheme(currentTheme()), []);

  const toggle = useCallback(() => {
    const next = currentTheme() === 'dark' ? 'light' : 'dark';
    document.documentElement.dataset.theme = next;
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch (_) {
      // Private browsing / storage disabled — the toggle still works for this page.
    }
    setTheme(next);
  }, []);

  const isDark = theme === 'dark';

  return (
    <button
      type="button"
      className="icon-btn"
      onClick={toggle}
      aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
      title={isDark ? 'Light theme' : 'Dark theme'}
    >
      {/* Both icons ship; CSS shows the right one so there is no hydration flash. */}
      <svg className="icon icon-sun" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <circle cx="12" cy="12" r="4.2" />
        <g strokeLinecap="round">
          <path d="M12 2.5v2.2M12 19.3v2.2M2.5 12h2.2M19.3 12h2.2M5.3 5.3l1.6 1.6M17.1 17.1l1.6 1.6M18.7 5.3l-1.6 1.6M6.9 17.1l-1.6 1.6" />
        </g>
      </svg>
      <svg className="icon icon-moon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5z" />
      </svg>
    </button>
  );
}

export default ThemeToggle;
